import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { COLORS, SHADOWS, FONTS } from '../styles/tokens';
import { APP_ROLE_PREFIX } from '../constants/auth-methods';
import { createUser, assignRoles, type KeycloakRole } from '../api/keycloak-admin';

interface CreateUserModalProps {
  appRoles: KeycloakRole[];
  allRoles: KeycloakRole[];
  onCreated: () => void;
  onClose: () => void;
}

/** Modal for admin to create a new user with optional role assignment */
export function CreateUserModal({ appRoles, allRoles, onCreated, onClose }: CreateUserModalProps): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adminRole = allRoles.find(r => r.name === 'admin');

  function toggleRole(roleName: string): void {
    setSelectedRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleName)) next.delete(roleName);
      else next.add(roleName);
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !username.trim() || !password) {
      setError('All fields are required');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      const userId = await createUser(token, {
        username: username.trim(),
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });

      const rolesToAssign: KeycloakRole[] = appRoles.filter(r => selectedRoles.has(r.name));
      if (isAdmin && adminRole) rolesToAssign.push(adminRole);
      if (rolesToAssign.length > 0) {
        await assignRoles(token, userId, rolesToAssign);
      }

      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    height: '38px', padding: '0 12px', borderRadius: '6px',
    border: `1px solid ${colors.border}`, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
    color: colors.text, fontFamily: FONTS.body, fontSize: '13px', outline: 'none', width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: colors.textSecondary, marginBottom: '4px', display: 'block',
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 50 }} />

      {/* Modal */}
      <div className="vx-modal-pad" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto',
        backgroundColor: colors.card, borderRadius: '12px',
        boxShadow: isDark ? SHADOWS.neumorphicDark : SHADOWS.neumorphicLight,
        zIndex: 51, padding: '32px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{
            fontFamily: FONTS.heading, fontSize: '14px', letterSpacing: '0.2em',
            textTransform: 'uppercase', color: colors.text, margin: 0,
          }}>
            CREATE USER
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: colors.textSecondary }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {error && (
          <div style={{ padding: '10px 14px', backgroundColor: isDark ? '#3b1c1c' : '#fde8e8', border: '1px solid #EF4444', borderRadius: '8px', color: '#EF4444', fontFamily: FONTS.body, fontSize: '12px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name row */}
          <div className="vx-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={labelStyle}>First Name</label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Last Name</label>
              <input value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Email */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
          </div>

          {/* Username */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Username</label>
            <input value={username} onChange={e => setUsername(e.target.value)} style={inputStyle} />
          </div>

          {/* Password row */}
          <div className="vx-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Password</label>
              <PasswordField value={password} onChange={setPassword} inputStyle={inputStyle} iconColor={colors.textSecondary} />
            </div>
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <PasswordField value={confirmPassword} onChange={setConfirmPassword} inputStyle={inputStyle} iconColor={colors.textSecondary} />
            </div>
          </div>

          {/* App Roles */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>App Access</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
              {appRoles.map(role => {
                const checked = selectedRoles.has(role.name);
                const displayName = role.name.replace(APP_ROLE_PREFIX, '');
                return (
                  <button
                    key={role.name}
                    type="button"
                    onClick={() => toggleRole(role.name)}
                    style={{
                      padding: '4px 12px', borderRadius: '4px', fontSize: '11px',
                      fontFamily: FONTS.body, cursor: 'pointer',
                      border: `1px solid ${checked ? COLORS.accent.blue : colors.border}`,
                      backgroundColor: checked ? (isDark ? 'rgba(1, 169, 237, 0.1)' : 'rgba(1, 169, 237, 0.05)') : 'transparent',
                      color: checked ? COLORS.accent.blue : colors.textSecondary,
                    }}
                  >
                    {displayName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Admin toggle */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isAdmin}
                onChange={e => setIsAdmin(e.target.checked)}
                style={{ accentColor: COLORS.accent.gold }}
              />
              <span style={{
                fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.1em',
                textTransform: 'uppercase', color: COLORS.accent.gold,
              }}>
                Vertex admin
              </span>
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', height: '42px', borderRadius: '6px', border: 'none',
              background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
              color: '#FFFFFF', fontFamily: FONTS.heading, fontSize: '12px', letterSpacing: '0.15em',
              textTransform: 'uppercase', cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Creating...' : 'Create User'}
          </button>
        </form>
      </div>
    </>
  );
}

interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  inputStyle: React.CSSProperties;
  iconColor: string;
}

/**
 * Password input with a show/hide visibility toggle. Defaults to masked;
 * the eye button flips the field to plain text so the admin can verify what
 * they typed. Leaves room on the right for the icon so it never overlaps text.
 */
function PasswordField({ value, onChange, inputStyle, iconColor }: PasswordFieldProps): React.JSX.Element {
  const [visible, setVisible] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <input
        type={visible ? 'text' : 'password'}
        autoComplete="new-password"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ ...inputStyle, paddingRight: '40px' }}
      />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute', right: '6px', top: 0, height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'none', border: 'none', cursor: 'pointer', padding: '0 6px', color: iconColor,
        }}
      >
        {visible ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}
